import {
  ConnectedPosition,
  FlexibleConnectedPositionStrategy,
  GlobalPositionStrategy,
  OverlayPositionBuilder as OPS,
} from '@angular/cdk/overlay';
import { ElementRef, Injectable, inject } from '@angular/core';

import {
  ConnectedPosition as CP,
  OverlayHorizontalPosition,
  OverlayPosition,
  OverlayVerticalPosition,
} from './overlay.interface';

@Injectable({
  providedIn: 'root',
})
export class OverlayPositionBuilder {
  private pb = inject(OPS);


  connectedPosition(
    origin: ElementRef | Element,
    position: CP | CP[] | string[] | string
  ): FlexibleConnectedPositionStrategy {
    return this.pb.flexibleConnectedTo(origin).withPositions(this._parseConnectedPositions(position)).withPush(false);
  }

  position({ horizontal, vertical }: OverlayPosition): GlobalPositionStrategy {
    const position = this.pb.global();
    this._parseHorizontalPosition(position, horizontal);
    this._parseVerticalPosition(position, vertical);

    return position;
  }

  private _parseVerticalPosition(builder: GlobalPositionStrategy, position: OverlayVerticalPosition) {
    switch (position) {
      case OverlayVerticalPosition.center:
      default:
        return builder.centerVertically();
      case OverlayVerticalPosition.bottom:
        return builder.bottom();
      case OverlayVerticalPosition.top:
        return builder.top();
    }
  }

  private _parseHorizontalPosition(builder: GlobalPositionStrategy, position: OverlayHorizontalPosition) {
    switch (position) {
      case OverlayHorizontalPosition.center:
      default:
        return builder.centerHorizontally();
      case OverlayHorizontalPosition.start:
        return builder.left();
      case OverlayHorizontalPosition.end:
        return builder.right();
    }
  }

  private _parseConnectedPositions(position: CP[] | CP | string[] | string): ConnectedPosition[] {
    const positions = [];

    if (typeof position === 'string') {
      positions.push(position);
    } else if (position) {
      positions.push(...position);
    } else {
      positions.push(CP.after);
    }

    return [...positions].map((value) => this._parseConnectedPosition(value));
  }

  private _parseConnectedPosition(position: CP | string): ConnectedPosition {
    const horizontal = { overlayY: 'center', originY: 'center' };
    const vertical = { overlayX: 'center', originX: 'center' };

    const above = { overlayY: 'bottom', originY: 'top' };
    const below = { overlayY: 'top', originY: 'bottom' };
    const before = { overlayX: 'end', originX: 'start' };
    const after = { overlayX: 'start', originX: 'end' };

    switch (position) {
      case CP.before:
      default:
        return { ...horizontal, ...before } as ConnectedPosition;
      case CP.after:
        return { ...horizontal, ...after } as ConnectedPosition;
      case CP.above:
        return { ...vertical, ...above } as ConnectedPosition;
      case CP.below:
        return { ...vertical, ...below } as ConnectedPosition;

      case CP.belowStart:
        return { ...below, ...before } as ConnectedPosition;
      case CP.belowEnd:
        return { ...below, ...after } as ConnectedPosition;
      case CP.aboveStart:
        return { ...above, ...before } as ConnectedPosition;
      case CP.aboveEnd:
        return { ...above, ...after } as ConnectedPosition;

      case CP.belowAlignLeft:
        return { ...below, ...before, overlayX: 'start' } as ConnectedPosition;
      case CP.belowAlignRight:
        return { ...below, ...after, overlayX: 'end' } as ConnectedPosition;
      case CP.aboveAlignLeft:
        return { ...above, ...before, overlayX: 'start' } as ConnectedPosition;
      case CP.aboveAlignRight:
        return { ...above, ...after, overlayX: 'end' } as ConnectedPosition;
    }
  }
}
